<script setup>
import {ref, computed, reactive, watch} from "vue";
import needsDashboardService from "@/services/needs-dashboard.service";
import {useStore} from "vue2-helpers/vuex";
import {baseURL} from "@/axios";

const store = useStore();

const showModal = ref(false);
const step = ref(1);

const form = reactive({
    goals: [],
    timeline: [],
    teamSize: []
});

const canSendRequest = computed(() =>
    form.goals.length > 0 &&
    form.timeline.length > 0 &&
    form.teamSize.length > 0
);

watch(() => store.state.showUpsell, value => {
    if (value) {
        open();
    }
})

watch(step, value => {
    if (value === 3) {
        sendRequest();
    }
});

async function sendRequest() {
    const {data} = await needsDashboardService.sendUpsellRequest(form);

    if (data) {
        store.state.user.needs_upsell_request_sent = 1;
    }
}

function open() {
    if (store.state.user.needs_upsell_request_sent) {
        step.value = 4;
    } else {
        step.value = 1;
    }

    showModal.value = true;
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" hide-footer hide-header modal-class="modal-upsell">
        <div v-if="step === 1" class="modal-content__inner modal-content__inner--dark">
            <header class="modal-header">                
                <button @click="showModal = false" class="close"></button>
            </header>

            <h5 class="modal-title">Unlock Full SwitchPitch Capabilities</h5>

            <ul class="list-upsell">
                <li>AI search with prioritized scoring</li>
                <li>Adoption-based market intelligence</li>
                <li>AI-generated custom reports</li>
            </ul>

            <button class="modal-upsell__button" @click="step++">Activate Full Access</button>

            <footer class="modal-footer">
                <a href="https://meetings.hubspot.com/marigona-xhemaili" target="_blank" class="link">Talk To An Expert</a>
            </footer>
        </div>

        <div v-if="step === 2" class="modal-content__inner">
            <header class="modal-header">                
                <button @click="showModal = false" class="close"></button>
            </header>

            <h5 class="heading5 heading5--secondary mb-4">What Are You Looking To Do?</h5>

            <div class="input-checkbox input-checkbox--secondary mb-4">
                <input id="el1" type="checkbox" value="find-startups" v-model="form.goals">
                <label for="el1">Find Startups</label>
            </div>

            <div class="input-checkbox input-checkbox--secondary mb-4">
                <input id="el2" type="checkbox" value="market-intel" v-model="form.goals">
                <label for="el2">Adoption-based market intelligence</label>
            </div>

            <div class="input-checkbox input-checkbox--secondary mb-4">
                <input id="el3" type="checkbox" value="ai-reports" v-model="form.goals">
                <label for="el3">AI-generated custom reports</label>
            </div>

            <h5 class="heading5 heading5--secondary mb-4">Timeline</h5>

            <div class="d-flex">
                <div class="input-radio mr-4 mb-4">
                    <input id="el4" type="radio" value="immediate" v-model="form.timeline">
                    <label for="el4">Immediate</label>
                </div>

                <div class="input-radio mr-4 mb-4">
                    <input id="el5" type="radio" value="this-quarter" v-model="form.timeline">
                    <label for="el5">This quarter</label>
                </div>
            </div>

            <h5 class="heading5 heading5--secondary mb-4">Team Size</h5>

            <div class="d-flex">
                <div class="input-radio mr-4 mb-4">
                    <input id="el6" type="radio" value="5-" v-model="form.teamSize">
                    <label for="el6">&lt;5</label>
                </div>

                <div class="input-radio mr-4 mb-4">
                    <input id="el7" type="radio" value="5-10" v-model="form.teamSize">
                    <label for="el7">5-10</label>
                </div>

                <div class="input-radio mr-4 mb-4">
                    <input id="el8" type="radio" value="11-20" v-model="form.teamSize">
                    <label for="el8">11-20</label>
                </div>

                <div class="input-radio mr-4 mb-4">
                    <input id="el9" type="radio" value="20+" v-model="form.teamSize">
                    <label for="el9">20+</label>
                </div>
            </div>
            
            <footer class="modal-footer">
                <button
                    class="modal-upsell__button modal-upsell__button--dark"
                    :disabled="!canSendRequest"
                    @click="step++"
                >
                    Contuinue
                </button>
            </footer>
        </div>

        <div v-if="step === 3" class="modal-content__inner modal-content__inner--dark">
            <header class="modal-header">                
                <button @click="showModal = false" class="close"></button>
            </header>

            <h5 class="modal-title mb-4 mb-sm-5">Here's A Preview Of<br/> Your Insights</h5>

            <figure class="modal-upsell__figure">
                <header class="modal-upsell__figure__header">Deep Dive Report</header>

                <img src="/dive-report21.png" alt="">

                <a :href="baseURL + '/files/Enterprise AI-deep-dive-report.pdf'" target="_blank"><figcaption class="modal-upsell__figure__footer">Download Sample</figcaption></a>
            </figure>
            
            <figcaption class="modal-upsell__figcaption">We'll set up your full access and customized environment.</figcaption>

            <footer class="modal-footer mt-5">
                <button
                    class="modal-upsell__button"
                    @click="step++"
                >
                    Contuinue Setup
                </button>
            </footer>
        </div>

        <div v-if="step === 4" class="modal-content__inner">
            <header class="modal-header">                
                <button @click="showModal = false" class="close"></button>
            </header>

            <h5 class="modal-title mb-4 mb-sm-5">Your SwitchPitch Access<br/> Is Being Prepared</h5>

            <h5 class="heading5 heading5--secondary mb-4">What's Next:</h5>

            <ul class="list-next">
                <li>You'll receive your agreement within 1 business day</li>
                <li>Your workspace will be pre-configured based on your needs</li>
                <li><b>Optional:</b> Schedule a 15-min setup call</li>
            </ul>

            <footer class="modal-footer modal-footer--center mt-3 mt-sm-5">
                <a
                    href="https://meetings.hubspot.com/marigona-xhemaili"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="modal-upsell__button modal-upsell__button--dark"
                >
                    Schedule Onboarding Call
                </a>

                <button @click="showModal = false" class="modal-upsell__button modal-upsell__button--light">Close</button>
            </footer>
        </div>

    </b-modal>
</template>

<style scoped>

</style>
